/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {DraggableProvidedDragHandleProps} from 'react-beautiful-dnd';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {FilterDrawerClosedIcon} from '../../icons/current/FilterDrawerClosedIcon';
import {FilterDrawerOpenIcon} from '../../icons/current/FilterDrawerOpenIcon';
import {MaterialDragIcon} from '../../icons/current/MaterialDragIcon';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';
import SelectionIndicator from '../status/SelectionIndicator';
import {FilterDrawerCategoryBase} from './types';

interface FilterDrawerCategoryRowProps {
  category: FilterDrawerCategoryBase;
  classes: Record<string, string>;
  // the list's drag handle - only the six-dot icon starts a reorder ...
  dragHandleProps?: DraggableProvidedDragHandleProps;
  expanded: boolean;
  index: number;
  onToggle: (id: string) => void;
}

/**
 * Renders one expandable filter category row inside a filter drawer.
 *
 * ```typescript
 * import FilterDrawerCategoryRow from '@ion/dls/src/components/drawer/FilterDrawerCategoryRow';
 *
 * ...
 *
 * <FilterDrawerCategoryRow category={category}
 *   classes={classes}
 *   dragHandleProps={dragHandleProps}
 *   expanded={expanded}
 *   index={index}
 *   onToggle={toggleCategory} />
 * ```
 */
export const FilterDrawerCategoryRow = memo(({
  category,
  classes,
  dragHandleProps,
  expanded,
  index,
  onToggle
}: FilterDrawerCategoryRowProps) => {
  const handleToggle = useCallback(() => {
    onToggle(category.id);
  }, [category.id, onToggle]);

  // a click on the handle neither moves the category nor opens it - only a
  // drag (or the handle's own keyboard reorder) moves it ...
  const onDragHandleClick = useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
  }, []);

  return (
    <div className={classnames(classes.category, {
      [classes.categoryFirst] : index === 0
    })}>
      <button type='button'
        className={classes.categoryHeader}
        onClick={handleToggle}
        aria-expanded={expanded}>
        <span className={classes.categoryHeaderContent}>
          <span {...dragHandleProps}
            className={classes.dragButton}
            role='button'
            tabIndex={0}
            aria-label={translate('filters:drawer.moveCategory', {label: category.label})}
            onClick={onDragHandleClick}>
            <MaterialDragIcon color={dlsColor('grey3')} size={IconSize.medium} />
          </span>
          <span className={classes.categoryLabel}>{category.label}</span>
          <Render.If condition={!!category.selectedCount}>
            <SelectionIndicator count={category.selectedCount || 0}
              label={translate('filters:drawer.selectedCount', {count: category.selectedCount})} />
          </Render.If>
        </span>
        <span className={classes.chevronButton}>
          <Render>
            <Render.If condition={expanded}>
              <FilterDrawerOpenIcon />
            </Render.If>
            <Render.Else>
              <FilterDrawerClosedIcon />
            </Render.Else>
          </Render>
        </span>
      </button>
      <div className={classnames(classes.categoryBodyShell, {
        [classes.categoryBodyShellExpanded] : expanded
      })}>
        <div className={classes.categoryBodyInner}>
          <div className={classes.categoryBody}>
            {category.content}
          </div>
        </div>
      </div>
    </div>
  );
});

export default FilterDrawerCategoryRow;
